/**
 * #920: is a failed commit a FAILURE, or one we already applied?
 *
 * `_onCommitFailure` logged every rejected commit as
 * `MLS commit failed (1/3) … ValidationError: Cannot process message, epoch too
 * old`, with a retry counter and six stack frames. On a healthy room joining for
 * the first time that line is a lie: the Welcome already advanced the agent to
 * the new epoch, so the 0→1 commit pulled afterwards from `mls_delivery_queue`
 * is one it has already applied.
 *
 * ⚠️ Read `_onCommitFailure`'s own comment before changing this. An earlier
 * attempt classified by SUBSTRING-MATCHING the error text for "epoch" / "old" /
 * "Validation", and `ValidationError: Desired gen in the past` contains
 * "Validation" — so the loudest real production failure was filed as benign,
 * 1,439 times on a single agent. That is why the code was deliberately left
 * with "no classification. COUNT."
 *
 * This is not that. It never looks at the error text. It compares the two
 * EPOCHS, which is protocol state rather than a message string:
 *
 *   msg <  group   the group is already past it  → already applied, benign
 *   msg >= group   it should have applied        → suspicious, count it
 *
 * The `>=` case is what preserves #474 and #629: a genuine divergence still
 * counts toward the resync ladder. Anything unknown counts too, so an
 * unparseable epoch can never silence a real failure — the failure mode of this
 * function is noise, never silence.
 */
export type CommitFailureDisposition = "already-applied" | "count";

/**
 * Normalise an epoch, accepting every representation the two sides actually use.
 *
 * ⚠️ This is the whole reason #849 did nothing in production. Its first version
 * guarded with `typeof x !== "number"` — but `MLSGroupManager.epoch` is a
 * **bigint** (`packages/crypto/src/mls-group.ts:169`), so the guard fired on
 * every call and the comparison was never reached. The delivery payload's epoch
 * arrives as a JSON number, so the two sides are genuinely different types.
 * Every test passed, because the mock declared `epoch = 5`.
 *
 * Comparing as bigint keeps the full BIGINT range the server column allows;
 * Number would lose precision past 2^53.
 *
 * Twin of `classifyDecryptFailure` in `packages/web/lib/mls-decrypt-recovery.ts`.
 * Copied rather than shared: the plugin cannot import from the web app, and
 * moving it into `@agentvault/crypto` would put a publish cycle in the path of a
 * log-noise fix. If one side changes, change both — they encode one protocol
 * rule, and #921 is what drift looks like.
 */
function toEpoch(v: unknown): bigint | null {
  if (typeof v === "bigint") return v;
  if (typeof v === "number") {
    return Number.isFinite(v) ? BigInt(Math.trunc(v)) : null;
  }
  if (typeof v === "string" && /^\d+$/.test(v)) return BigInt(v);
  return null;
}

export function classifyCommitFailure(
  messageEpoch: unknown,
  groupEpoch: unknown,
): CommitFailureDisposition {
  const msg = toEpoch(messageEpoch);
  const grp = toEpoch(groupEpoch);
  // Unknown on either side must COUNT. Guessing "already applied" here would
  // re-create the 1,439-swallowed-failures bug through a different door.
  if (msg === null || grp === null) return "count";
  return msg < grp ? "already-applied" : "count";
}
